import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicReport } from '../services/reportBuilderService';
import KPICardWidget from '../components/ReportBuilder/widgets/KPICardWidget';
import LineChartWidget from '../components/ReportBuilder/widgets/LineChartWidget';
import BarChartWidget from '../components/ReportBuilder/widgets/BarChartWidget';
import PieChartWidget from '../components/ReportBuilder/widgets/PieChartWidget';
import DataTableWidget from '../components/ReportBuilder/widgets/DataTableWidget';
import TextBlockWidget from '../components/ReportBuilder/widgets/TextBlockWidget';
import ScoreGaugeWidget from '../components/ReportBuilder/widgets/ScoreGaugeWidget';
import ComparisonWidget from '../components/ReportBuilder/widgets/ComparisonWidget';
import DividerWidget from '../components/ReportBuilder/widgets/DividerWidget';
import ImageBlockWidget from '../components/ReportBuilder/widgets/ImageBlockWidget';

const WIDGET_MAP = {
  kpi_card: KPICardWidget,
  line_chart: LineChartWidget,
  bar_chart: BarChartWidget,
  pie_chart: PieChartWidget,
  data_table: DataTableWidget,
  text_block: TextBlockWidget,
  score_gauge: ScoreGaugeWidget,
  comparison: ComparisonWidget,
  divider: DividerWidget,
  image_block: ImageBlockWidget
};

export default function PublicReportViewer() {
  const { shareId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getPublicReport(shareId);
        setReport(data.template);
      } catch (err) {
        setError('This report is not available or has been made private.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Report Not Available</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const widgets = typeof report.widgets === "string" ? JSON.parse(report.widgets) : (report.widgets || []);
  const layout = typeof report.layout === "string" ? JSON.parse(report.layout) : (report.layout || []);

  // Sort widgets by their layout position (top to bottom, left to right)
  const sortedWidgets = [...widgets].sort((a, b) => {
    const la = layout.find(l => l.i === a.id) || { y: 0, x: 0 };
    const lb = layout.find(l => l.i === b.id) || { y: 0, x: 0 };
    if (la.y !== lb.y) return la.y - lb.y;
    return la.x - lb.x;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{report.name}</h1>
              {report.description && <p className="text-sm text-gray-500 mt-0.5">{report.description}</p>}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Last updated</p>
              <p className="text-sm text-gray-600">{new Date(report.updatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="space-y-4">
          {sortedWidgets.map(widget => {
            const WidgetComponent = WIDGET_MAP[widget.type];
            if (!WidgetComponent) return null;
            const layoutItem = layout.find(l => l.i === widget.id);
            return (
              <div key={widget.id} className="bg-white rounded-lg shadow-sm border p-4">
                <WidgetComponent config={widget.config || {}} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t bg-white mt-12">
        <div className="max-w-5xl mx-auto px-6 py-4 text-center">
          <p className="text-xs text-gray-400">Powered by <span className="font-semibold text-indigo-500">SERPfinity</span> - AI-Powered Search Visibility Platform</p>
        </div>
      </div>
    </div>
  );
}
